import React, { createContext, useContext, useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link } from 'react-router-dom';
import { Cookie, ShieldCheck, BarChart3, Megaphone } from 'lucide-react';

// --- Types & Constants ---

type ConsentCategories = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

interface CookieConsentContextType {
  consent: ConsentCategories;
  hasInteracted: boolean;
  isOpen: boolean; // For the settings modal
  openSettings: () => void;
  closeSettings: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (categories: ConsentCategories) => void;
  showBanner: boolean;
  closeBanner: () => void;
}

const COOKIE_NAME = 'cookie_consent';
const COOKIE_EXPIRATION_DAYS = 180;

const defaultConsent: ConsentCategories = {
  essential: true,
  analytics: false,
  marketing: false,
};

// --- Helpers ---

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/;SameSite=Lax";
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// --- Context ---

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// --- Provider ---

export const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [consent, setConsent] = useState<ConsentCategories>(defaultConsent);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const storedConsent = getCookie(COOKIE_NAME);
    if (storedConsent) {
      try {
        setConsent(JSON.parse(storedConsent));
        setHasInteracted(true);
        setShowBanner(false);
      } catch (e) {
        // Invalid cookie, reset
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const saveToCookie = (preferences: ConsentCategories) => {
    setConsent(preferences);
    setHasInteracted(true);
    setCookie(COOKIE_NAME, JSON.stringify(preferences), COOKIE_EXPIRATION_DAYS);
    setShowBanner(false);
    setIsModalOpen(false);
    
    if (!preferences.analytics) {
      console.log("Analytics disabled");
    }
  };

  const acceptAll = () => {
    saveToCookie({ essential: true, analytics: true, marketing: true });
  };

  const rejectAll = () => {
    saveToCookie({ essential: true, analytics: false, marketing: false });
  };

  const savePreferences = (categories: ConsentCategories) => {
    saveToCookie(categories);
  };

  return (
    <CookieConsentContext.Provider value={{
      consent,
      hasInteracted,
      isOpen: isModalOpen,
      openSettings: () => setIsModalOpen(true),
      closeSettings: () => setIsModalOpen(false),
      acceptAll,
      rejectAll,
      savePreferences,
      showBanner,
      closeBanner: () => setShowBanner(false)
    }}>
      {children}
      <CookieBanner />
      <CookieSettingsModal />
    </CookieConsentContext.Provider>
  );
};

// --- Components ---

const CookieBanner = () => {
  const { showBanner, acceptAll, rejectAll, openSettings } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-slate-900 border-t border-slate-800 shadow-[0_-4px_30px_rgba(0,0,0,0.15)] animate-in slide-in-from-bottom-full duration-500 ease-out" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-5 max-w-[1200px]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-5 md:gap-8">
          
          {/* Content Left */}
          <div className="flex-1 w-full md:w-auto">
            <div className="flex items-start md:items-center gap-4">
              <div className="p-2.5 bg-slate-800 rounded-xl text-blue-400 shrink-0 hidden md:block">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="space-y-1.5 md:space-y-1">
                <p className="text-sm md:text-base font-medium text-white leading-tight">
                  פרטיות לפני הכל
                </p>
                <p className="text-xs md:text-sm text-slate-400 leading-relaxed max-w-2xl">
                  אנו משתמשים בקוקיז כדי לשפר את חווית הגלישה שלך. חלקן חיוניות לתפעול האתר ואחרות עוזרות לנו להבין איך לשפר אותו.
                  <span className="hidden md:inline mx-2 text-slate-600">|</span>
                  <br className="md:hidden" />
                  <button onClick={openSettings} className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors mr-0 md:mr-1">
                    הגדרות מתקדמות
                  </button>
                  <span className="mx-2 text-slate-600">•</span>
                  <Link to="/terms" className="text-slate-400 hover:text-slate-200 transition-colors">
                    תנאי שימוש
                  </Link>
                  <span className="mx-2 text-slate-600">•</span>
                  <Link to="/privacy" className="text-slate-400 hover:text-slate-200 transition-colors">
                    מדיניות פרטיות
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Actions Right */}
          <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
            <Button 
              onClick={rejectAll} 
              variant="ghost" 
              className="flex-1 md:flex-none h-10 md:h-11 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-700 md:border-transparent text-sm"
            >
              דחה הכל
            </Button>
            <Button 
              onClick={acceptAll} 
              className="flex-1 md:flex-none h-10 md:h-11 px-6 md:px-8 rounded-xl bg-white text-slate-900 hover:bg-slate-100 hover:scale-[1.02] transition-all font-bold text-sm shadow-lg shadow-slate-900/20"
            >
              אשר הכל
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

const CookieSettingsModal = () => {
  const { isOpen, closeSettings, consent, savePreferences } = useCookieConsent();
  const [localConsent, setLocalConsent] = useState<ConsentCategories>(defaultConsent);

  // Sync local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalConsent(consent);
    }
  }, [isOpen, consent]);

  const handleSave = () => {
    savePreferences(localConsent);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeSettings()}>
      <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden rounded-2xl z-[10000]" dir="rtl">
        <DialogHeader className="p-6 pb-2 text-right">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Cookie className="w-5 h-5 text-slate-900" />
            הגדרות קוקיז
          </DialogTitle>
          <DialogDescription>
            נהל את ההעדפות שלך לגבי קבצי קוקיז באתר זה.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-2 space-y-6 max-h-[60vh] overflow-y-auto">
          
          {/* Essential */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="flex gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="space-y-1">
                <Label className="text-base font-semibold text-slate-900">חיוניים (תמיד פעיל)</Label>
                <p className="text-sm text-slate-500 leading-relaxed">
                  קבצים אלו נחוצים לתפקוד התקין של האתר (למשל: אבטחה, התחברות, שמירת העדפות קוקיז). לא ניתן לבטל אותם.
                </p>
              </div>
            </div>
            <Switch checked={true} disabled />
          </div>

          {/* Analytics */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="space-y-1">
                <Label htmlFor="analytics" className="text-base font-semibold text-slate-900">סטטיסטיקה וביצועים</Label>
                <p className="text-sm text-slate-500 leading-relaxed">
                  עוזרים לנו להבין כיצד גולשים משתמשים באתר, אילו דפים פופולריים ואיפה יש תקלות, כדי שנוכל לשפר את השירות.
                </p>
              </div>
            </div>
            <Switch 
              id="analytics" 
              checked={localConsent.analytics} 
              onCheckedChange={(checked) => setLocalConsent(prev => ({ ...prev, analytics: checked }))} 
            />
          </div>

          {/* Marketing */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <Megaphone className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="space-y-1">
                <Label htmlFor="marketing" className="text-base font-semibold text-slate-900">שיווק ופרסום</Label>
                <p className="text-sm text-slate-500 leading-relaxed">
                  מאפשרים לנו להציג לך תוכן מותאם אישית ופרסומות רלוונטיות בפלטפורמות אחרות.
                </p>
              </div>
            </div>
            <Switch 
              id="marketing" 
              checked={localConsent.marketing} 
              onCheckedChange={(checked) => setLocalConsent(prev => ({ ...prev, marketing: checked }))} 
            />
          </div>

        </div>

        <DialogFooter className="p-6 border-t border-slate-100 bg-slate-50/50 gap-2 sm:gap-0">
          <Button variant="outline" onClick={closeSettings} className="rounded-xl border-slate-200">ביטול</Button>
          <Button onClick={handleSave} className="rounded-xl bg-slate-900 hover:bg-slate-800">שמור העדפות</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};